import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Tablet, PowerOff, History, ChevronDown, ChevronRight, Upload, X, ImagePlus, UserCircle2, UserPlus, ArrowLeftRight } from "lucide-react";
import { api, fileUrl, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { hueForCharter } from "@/lib/charterHues";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { fmtDateTime, useAppTz } from "@/lib/tz";

const empty = {
  name: "", active: true, ipad_pin: "", beds: 2,
  cover_image_path: null, image_paths: [], sort_order: 100,
  occupancy_status: "guest",
};

const OCCUPANCY_OPTIONS = [
  { value: "guest",         label: "Guest",         desc: "Bookable",     bg: "bg-emerald-500/15 border-emerald-500/40 text-emerald-700 dark:text-emerald-400" },
  { value: "available",     label: "Available",     desc: "Bookable",     bg: "bg-sky-500/15 border-sky-500/40 text-sky-700 dark:text-sky-400" },
  { value: "crew",          label: "Crew use",      desc: "Spa locked",   bg: "bg-amber-500/15 border-amber-500/40 text-amber-700 dark:text-amber-500" },
  { value: "not_available", label: "Not available", desc: "Spa locked",   bg: "bg-slate-500/15 border-slate-500/40 text-slate-700 dark:text-slate-400" },
];

export default function CabinsPanel() {
  const { admin } = useAuth();
  const canKill = admin?.role === "developer" || admin?.role === "admin";
  const tz = useAppTz();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [uploadingGallery, setUploadingGallery] = useState(false);
  // Percentage (0-100) of the currently-uploading file. For gallery uploads
  // we also track which file index in the batch we're on so operators see
  // both "photo 2 of 5" and its individual progress. `null` = idle.
  const [coverProgress, setCoverProgress] = useState(null);
  const [galleryProgress, setGalleryProgress] = useState(null); // { index, total, percent }
  const [timeline, setTimeline] = useState([]);
  const [timelineOpen, setTimelineOpen] = useState(false);
  const [historyForCabin, setHistoryForCabin] = useState(null); // cabin id whose history is expanded

  const load = async () => {
    const { data } = await api.get("/admin/cabins");
    setItems(data || []);
  };
  const loadTimeline = async () => {
    try {
      const { data } = await api.get("/admin/arrangements/timeline");
      setTimeline(data || []);
    } catch (e) {
      console.debug("[Cabins] arrangement timeline load skipped:", e?.response?.status || e);
    }
  };
  useEffect(() => { load(); loadTimeline(); }, []);

  const perCabinHistory = React.useMemo(() => {
    const map = {};
    for (const row of timeline || []) {
      for (const g of (row.guests || [])) {
        const cid = g.cabin_id;
        if (!cid) continue;
        if (!map[cid]) map[cid] = [];
        map[cid].push({
          arrangement_id: row.id,
          effective_from: row.effective_from,
          effective_to: row.effective_to,
          status: row.status,
          charter_name: row.charter_name,
          guest_name: g.name,
          guest_female: g.female,
        });
      }
    }
    Object.keys(map).forEach((k) => map[k].sort((a, b) => (b.effective_from || "").localeCompare(a.effective_from || "")));
    return map;
  }, [timeline]);

  // Live map of "which guest(s) are living in this cabin right now" — now
  // sourced from /admin/cabins.current_occupants (backend joins live
  // charter.guests[] against active charters + currently-onboard check).
  // Previous implementation used /admin/arrangements/timeline snapshots,
  // which drifted out of sync with the Trips-panel edits and caused the
  // berth counter to disagree with the "Currently" chips on the same card.
  const currentOccupants = React.useMemo(() => {
    const map = {};
    for (const c of items || []) {
      if ((c.current_occupants || []).length > 0) {
        map[c.id] = c.current_occupants;
      }
    }
    return map;
  }, [items]);

  // Shared uploader — used for both the cover and each gallery photo. Uses the
  // existing generic `/admin/upload` endpoint so we don't need any new backend
  // wiring; the returned `path` is what we persist on the cabin document. The
  // optional `onProgress(percent)` callback wires into axios' upload progress
  // stream so we can render a real-time bar (matters on cellular iPad links).
  const uploadOne = async (file, onProgress) => {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/admin/upload", fd, {
      headers: { "Content-Type": "multipart/form-data" },
      onUploadProgress: (evt) => {
        if (!onProgress) return;
        // `total` is missing on some Safari versions — fall back to the byte
        // count on the file so the bar still moves.
        const total = evt.total || file.size || 0;
        const pct = total > 0 ? Math.min(100, Math.round((evt.loaded * 100) / total)) : 0;
        onProgress(pct);
      },
    });
    return data.path;
  };

  const uploadCover = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingCover(true);
    setCoverProgress(0);
    try {
      const path = await uploadOne(file, setCoverProgress);
      setForm((f) => ({ ...f, cover_image_path: path }));
      toast.success("Cover photo uploaded");
    } catch (err) { toast.error(formatErr(err?.response?.data?.detail) || "Cover upload failed"); }
    finally { setUploadingCover(false); setCoverProgress(null); e.target.value = ""; }
  };

  const uploadGallery = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploadingGallery(true);
    setGalleryProgress({ index: 1, total: files.length, percent: 0 });
    try {
      // Sequential upload so failures don't leave stale entries. Small files,
      // small counts — a queue is overkill for a cabin gallery.
      const paths = [];
      for (let i = 0; i < files.length; i += 1) {
        const file = files[i];
        try {
          paths.push(await uploadOne(file, (pct) =>
            setGalleryProgress({ index: i + 1, total: files.length, percent: pct })
          ));
        } catch (err) { toast.error(`${file.name}: ${formatErr(err?.response?.data?.detail) || "upload failed"}`); }
      }
      if (paths.length) {
        setForm((f) => ({ ...f, image_paths: [...(f.image_paths || []), ...paths] }));
        toast.success(`Added ${paths.length} photo${paths.length === 1 ? "" : "s"}`);
      }
    } finally { setUploadingGallery(false); setGalleryProgress(null); e.target.value = ""; }
  };

  const removeGalleryAt = (idx) => {
    setForm((f) => ({ ...f, image_paths: (f.image_paths || []).filter((_, i) => i !== idx) }));
  };

  const save = async () => {
    try {
      const payload = {
        name: form.name,
        active: form.active,
        ipad_pin: form.ipad_pin || null,
        beds: parseInt(form.beds, 10) || 2,
        cover_image_path: form.cover_image_path || null,
        image_paths: form.image_paths || [],
        sort_order: Number.isFinite(parseInt(form.sort_order, 10)) ? parseInt(form.sort_order, 10) : 100,
        occupancy_status: form.occupancy_status || "guest",
      };
      if (form.id) await api.put(`/admin/cabins/${form.id}`, payload);
      else await api.post("/admin/cabins", payload);
      toast.success("Saved");
      setOpen(false); load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || "Could not save cabin"); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Remove cabin "${c.name}"?`)) return;
    await api.delete(`/admin/cabins/${c.id}`);
    load();
  };

  const killSession = async (c) => {
    if (!window.confirm(`Kill the iPad session for "${c.name}"? The device will be forced to log in again.`)) return;
    try {
      await api.post(`/admin/cabins/${c.id}/kill-ipad-session`);
      toast.success(`iPad session revoked for ${c.name}`);
      load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail) || "Could not revoke iPad session"); }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="font-display text-3xl">Cabins</h2>
          <p className="text-sm text-muted-foreground mt-1">Rooms on the yacht. Used to identify guests during a charter. <span className="text-accent">All timeline times shown in {tz}.</span></p>
        </div>
        <Button onClick={() => { setForm(empty); setOpen(true); }} className="rounded-full bg-primary hover:bg-primary/90" data-testid="new-cabin-btn">
          <Plus className="h-4 w-4 mr-2" /> New cabin
        </Button>
      </div>

      {/* Global cabin-arrangement timeline (collapsible) */}
      <div className="mb-6 border border-border bg-card" data-testid="global-timeline-block">
        <button
          type="button"
          onClick={() => setTimelineOpen((v) => !v)}
          className="w-full flex items-center justify-between p-4 hover:bg-muted/30"
          data-testid="toggle-global-timeline"
        >
          <span className="flex items-center gap-2 font-display text-lg">
            <History className="h-4 w-4 text-accent" /> Global arrangement timeline
            <Badge variant="outline" className="ml-2">{(timeline || []).length}</Badge>
          </span>
          {timelineOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </button>
        {timelineOpen && (
          <div className="border-t border-border p-4 space-y-2 max-h-[50vh] overflow-y-auto" data-testid="global-timeline-list">
            {(timeline || []).length === 0 && (
              <div className="text-sm text-muted-foreground">No arrangements recorded yet. Create a charter with guests to populate the timeline.</div>
            )}
            {(timeline || []).map((row) => (
              <div key={row.id} className={`border p-3 ${row.status === "active" ? "border-emerald-500/40 bg-emerald-500/5" : "border-border bg-background"}`} data-testid={`gt-row-${row.id}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <div className="text-sm font-medium">
                      {row.charter_name || "Charter"} · v{row.version}
                      {row.corrects_arrangement_id && <span className="text-accent ml-2">correction</span>}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {fmtDateTime(row.effective_from)}{row.effective_to && <> → {fmtDateTime(row.effective_to)}</>}
                    </div>
                  </div>
                  <Badge variant="outline" className={
                    row.status === "active" ? "border-emerald-500/40 text-emerald-700 dark:text-emerald-400" :
                    row.status === "scheduled" ? "border-sky-500/40 text-sky-700 dark:text-sky-400" :
                    "border-border text-muted-foreground"
                  }>{row.status}</Badge>
                </div>
                <div className="mt-2 text-xs text-muted-foreground">
                  {(row.guests || []).map((g) => (
                    <span key={g.guest_id} className="inline-block mr-3">
                      <span className="text-foreground">{g.name}</span> → {g.cabin_name}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Cabin grid — 4 columns on lg+ per operator's request. Cards start with a
          16:9 cover photo (or subtle placeholder), followed by the compact info
          panel. Sort order comes from the backend which pre-sorts by
          sort_order → name so any manual re-ordering is respected here. */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="cabins-grid">
        {items.map((c) => {
          const hasIpadSession = !!c.ipad_session_token;
          const history = perCabinHistory[c.id] || [];
          const occupants = currentOccupants[c.id] || [];
          const expanded = historyForCabin === c.id;
          const galleryCount = (c.image_paths || []).length;
          // Charter-hue palette — every cabin belonging to the same active
          // charter shares one distinctive accent (left rail + berth
          // colour + charter chip). Overlapping charters instantly stand
          // apart on the fleet grid.
          const hue = hueForCharter(c.active_charter_id);
          const beds = c.beds || 2;
          // Build one berth-slot per bed so the grid always shows the true
          // capacity (`[👤 MR] [👤 Miss] [+]` for a 3-bed cabin with two
          // guests). Occupied slots come first, remainder padded with
          // "free" placeholders so the row length equals `beds`.
          const berthSlots = [];
          for (let i = 0; i < beds; i++) {
            const occ = occupants[i];
            berthSlots.push(occ ? { free: false, occ } : { free: true, occ: null });
          }
          return (
            <div
              key={c.id}
              className={`bg-card border border-border border-l-4 ${hue.borderLeft} overflow-hidden flex flex-col transition-shadow`}
              data-testid={`cabin-${c.id}`}
            >
              {/* Cover image — click to open edit dialog for quick photo swaps. */}
              <button
                type="button"
                onClick={() => { setForm({ ...empty, ...c }); setOpen(true); }}
                className="relative block aspect-video bg-muted group"
                data-testid={`cabin-cover-${c.id}`}
                aria-label={`Edit ${c.name}`}
              >
                {c.cover_image_path ? (
                  <img
                    src={fileUrl(c.cover_image_path)}
                    alt={`${c.name} cover`}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center text-primary/30 gap-1">
                    <ImagePlus className="h-6 w-6" strokeWidth={1.5} />
                    <span className="text-[10px] uppercase tracking-widest">Add cover</span>
                  </div>
                )}
                {galleryCount > 0 && (
                  <span className="absolute bottom-2 right-2 bg-background/85 backdrop-blur px-2 py-0.5 text-[10px] font-mono">
                    +{galleryCount} photo{galleryCount === 1 ? "" : "s"}
                  </span>
                )}
                {/* Manual sort-order badge (top-left) so the operator can see
                    the current ordering weight at a glance without opening the
                    edit dialog. */}
                <span className="absolute top-2 left-2 bg-background/85 backdrop-blur px-2 py-0.5 text-[10px] font-mono text-muted-foreground">
                  #{c.sort_order ?? 100}
                </span>
              </button>

              <div className="p-4 flex-1 flex flex-col">
                <div className="flex items-start justify-between mb-2 gap-2">
                  <div className="min-w-0">
                    <div className="font-display text-lg truncate">{c.name}</div>
                    <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                      <Badge variant={c.active ? "default" : "outline"} className={c.active ? "bg-primary text-[10px]" : "text-[10px]"}>{c.active ? "Active" : "Hidden"}</Badge>
                      {hasIpadSession && (
                        <Badge className="bg-accent/25 text-accent border-0 hover:bg-accent/25 text-[10px]" data-testid={`cabin-ipad-connected-${c.id}`}>
                          <Tablet className="h-3 w-3 mr-1" /> iPad
                        </Badge>
                      )}
                      {/* Occupancy chip — always shown so the operator can
                          scan the fleet state at a glance without opening
                          each edit dialog. Colour mirrors the edit-dialog
                          picker so admins learn one visual vocabulary. */}
                      {(() => {
                        const opt = OCCUPANCY_OPTIONS.find((o) => o.value === (c.occupancy_status || "guest"));
                        if (!opt) return null;
                        return (
                          <Badge
                            className={`text-[10px] border ${opt.bg} hover:${opt.bg}`}
                            data-testid={`cabin-occupancy-${c.id}`}
                            title={opt.desc}
                          >
                            {opt.label}
                          </Badge>
                        );
                      })()}
                    </div>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <Button size="sm" variant="outline" onClick={() => { setForm({ ...empty, ...c }); setOpen(true); }} data-testid={`edit-cabin-${c.id}`}><Pencil className="h-3 w-3" /></Button>
                    <Button size="sm" variant="outline" onClick={() => remove(c)} className="text-destructive border-destructive/40" data-testid={`delete-cabin-${c.id}`}><Trash2 className="h-3 w-3" /></Button>
                  </div>
                </div>
                {/* Berth visualisation + charter chip.
                    Each bed renders as a distinct box: occupied berths show
                    the guest's photo/initial + first name, free berths show
                    a "+" ghost slot so operators immediately see where a
                    new passenger can go. The whole row is colour-tinted by
                    the active charter's hue so overlapping trips are
                    visually distinct on the fleet grid. */}
                <div
                  className="mb-2"
                  data-testid={`cabin-berths-${c.id}`}
                >
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-[10px] uppercase tracking-widest text-primary/60">
                      Berths
                      <span className="text-muted-foreground normal-case tracking-normal ml-1.5">
                        {c.berths_used || 0}/{beds} {c.is_full ? "· full" : (c.berths_free || 0) === beds ? "· empty" : `· ${c.berths_free} free`}
                      </span>
                    </span>
                    {c.active_charter_name && (
                      <span
                        className={`inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full ${hue.chipBg} ${hue.chipText}`}
                        title="Active charter"
                        data-testid={`cabin-charter-chip-${c.id}`}
                      >
                        <span className={`w-1.5 h-1.5 rounded-full ${hue.dot}`} aria-hidden />
                        <span className="truncate max-w-[9rem]">{c.active_charter_name}</span>
                      </span>
                    )}
                  </div>
                  <div className={`grid gap-1.5`} style={{ gridTemplateColumns: `repeat(${Math.min(beds, 3)}, minmax(0, 1fr))` }}>
                    {berthSlots.map((slot, idx) => {
                      const cabinLocked = ["crew", "not_available"].includes(c.occupancy_status);
                      if (slot.free) {
                        const disabled = cabinLocked;
                        // Free berth opens a small chooser: "Add new guest"
                        // (existing add-guest flow, pre-filling this cabin)
                        // or "Move existing guest here" — which jumps to
                        // Trips and opens the New Cabin Arrangement dialog
                        // so a passenger can be rescheduled into this
                        // berth from another cabin.
                        return (
                          <Popover key={`free-${idx}`}>
                            <PopoverTrigger asChild>
                              <button
                                type="button"
                                disabled={disabled}
                                className={`w-full text-left border border-dashed p-2 flex items-center gap-1.5 transition-colors ${
                                  disabled
                                    ? "border-border/40 bg-muted/20 text-muted-foreground/50 cursor-not-allowed"
                                    : "border-border/70 bg-background/50 text-muted-foreground hover:border-accent hover:bg-accent/5 hover:text-accent focus:outline-none focus:ring-2 focus:ring-accent/40"
                                }`}
                                data-testid={`cabin-berth-free-${c.id}-${idx}`}
                                title={disabled ? "Cabin is not available for guests" : "Fill this berth"}
                              >
                                <span className="w-6 h-6 rounded-full bg-muted border border-border/60 flex items-center justify-center flex-shrink-0">
                                  <Plus className="h-3 w-3" strokeWidth={2} />
                                </span>
                                <span className="text-[10px] uppercase tracking-widest truncate">
                                  {disabled ? (c.occupancy_status === "crew" ? "Crew" : "Locked") : "Free"}
                                </span>
                              </button>
                            </PopoverTrigger>
                            {!disabled && (
                              <PopoverContent className="w-64 p-2" align="start" data-testid={`cabin-berth-free-menu-${c.id}-${idx}`}>
                                <div className="text-[10px] uppercase tracking-widest text-primary/60 mb-2 px-1">Fill {c.name}</div>
                                <button
                                  type="button"
                                  className="w-full text-left flex items-center gap-2.5 px-2 py-2 rounded hover:bg-accent/10 text-sm transition-colors"
                                  onClick={() => window.dispatchEvent(new CustomEvent("yspa:intent", { detail: {
                                    kind: "add_guest_to_cabin",
                                    cabin_id: c.id,
                                    cabin_name: c.name,
                                    charter_id: c.active_charter_id || null,
                                  }}))}
                                  data-testid={`cabin-berth-add-new-${c.id}-${idx}`}
                                >
                                  <UserPlus className="h-4 w-4 text-accent flex-shrink-0" />
                                  <div className="min-w-0 flex-1">
                                    <div className="font-medium leading-tight">Add new guest</div>
                                    <div className="text-[11px] text-muted-foreground leading-tight mt-0.5">Create a fresh passenger record</div>
                                  </div>
                                </button>
                                <button
                                  type="button"
                                  className="w-full text-left flex items-center gap-2.5 px-2 py-2 rounded hover:bg-accent/10 text-sm transition-colors mt-1"
                                  onClick={() => window.dispatchEvent(new CustomEvent("yspa:intent", { detail: {
                                    kind: "schedule_move_to_cabin",
                                    cabin_id: c.id,
                                    cabin_name: c.name,
                                    charter_id: c.active_charter_id || null,
                                  }}))}
                                  data-testid={`cabin-berth-schedule-move-${c.id}-${idx}`}
                                >
                                  <ArrowLeftRight className="h-4 w-4 text-accent flex-shrink-0" />
                                  <div className="min-w-0 flex-1">
                                    <div className="font-medium leading-tight">Move existing guest here</div>
                                    <div className="text-[11px] text-muted-foreground leading-tight mt-0.5">Schedule a cabin rearrangement</div>
                                  </div>
                                </button>
                              </PopoverContent>
                            )}
                          </Popover>
                        );
                      }
                      return (
                        <button
                          key={`occ-${slot.occ.guest_id || idx}`}
                          type="button"
                          onClick={() => window.dispatchEvent(new CustomEvent("yspa:intent", { detail: {
                            kind: "highlight_guest",
                            guest_id: slot.occ.guest_id,
                            charter_id: slot.occ.charter_id || c.active_charter_id || null,
                          }}))}
                          className={`text-left border ${hue.berthOccupied} p-2 flex items-center gap-1.5 transition-shadow hover:shadow-md hover:ring-1 ${hue.ring} focus:outline-none focus:ring-2 focus:ring-accent/40`}
                          data-testid={`cabin-berth-occ-${c.id}-${idx}`}
                          title={`Open ${slot.occ.guest_name} in Trips & guests${slot.occ.charter_name ? ` · ${slot.occ.charter_name}` : ""}`}
                        >
                          {slot.occ.image_path ? (
                            <img
                              src={fileUrl(slot.occ.image_path)}
                              alt=""
                              loading="lazy"
                              decoding="async"
                              className="w-6 h-6 rounded-full object-cover border border-border/60 flex-shrink-0"
                              data-testid={`cabin-berth-avatar-${c.id}-${idx}`}
                            />
                          ) : (
                            <span className="w-6 h-6 rounded-full bg-background border border-border/60 flex items-center justify-center flex-shrink-0">
                              <span className="text-[10px] font-medium">
                                {(slot.occ.guest_name || "?").slice(0, 1).toUpperCase()}
                              </span>
                            </span>
                          )}
                          <span className="text-[11px] font-medium truncate">{slot.occ.guest_name}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
                {canKill && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => killSession(c)}
                    disabled={!hasIpadSession}
                    className="w-full text-destructive border-destructive/40 disabled:opacity-40 mb-2 text-[11px] h-8"
                    data-testid={`kill-ipad-${c.id}`}
                  >
                    <PowerOff className="h-3 w-3 mr-1.5" />
                    {hasIpadSession ? "Kill iPad session" : "No active iPad"}
                  </Button>
                )}
                <button
                  type="button"
                  onClick={() => setHistoryForCabin(expanded ? null : c.id)}
                  className="mt-auto w-full flex items-center justify-between text-[11px] text-primary/70 hover:text-primary border-t border-border pt-2"
                  data-testid={`cabin-history-toggle-${c.id}`}
                >
                  <span className="flex items-center gap-1"><History className="h-3 w-3" /> Occupancy <span className="text-muted-foreground">({history.length})</span></span>
                  {expanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                </button>
                {expanded && (
                  <div className="mt-2 space-y-1.5 text-xs" data-testid={`cabin-history-${c.id}`}>
                    {history.length === 0 && <div className="text-muted-foreground">No history yet.</div>}
                    {history.map((h, idx) => (
                      <div key={`${h.arrangement_id}-${idx}`} className={`border-l-2 pl-2 ${h.status === "active" ? "border-emerald-500" : h.status === "scheduled" ? "border-sky-500" : "border-border"}`}>
                        <div className="font-medium">{h.guest_name}{h.guest_female && <span className="text-muted-foreground"> · f</span>}</div>
                        <div className="text-muted-foreground">
                          {fmtDateTime(h.effective_from)}{h.effective_to && <> → {fmtDateTime(h.effective_to)}</>}
                          {h.charter_name && <span className="ml-1">· {h.charter_name}</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        })}
        {items.length === 0 && <div className="col-span-full text-muted-foreground text-sm">No cabins yet. Add one to start assigning guests.</div>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="cabin-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">{form.id ? "Edit" : "New"} cabin</DialogTitle></DialogHeader>
          <div className="space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="sm:col-span-2">
                <Label className="overline text-primary/60">Cabin name</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Master Suite" data-testid="cf-name" className="mt-2" />
              </div>
              <div>
                <Label className="overline text-primary/60">Sort order</Label>
                <Input
                  type="number"
                  value={form.sort_order ?? 100}
                  onChange={(e) => setForm({ ...form, sort_order: e.target.value === "" ? "" : parseInt(e.target.value, 10) })}
                  data-testid="cf-sort-order"
                  className="mt-2 font-mono"
                />
                <p className="text-[11px] text-muted-foreground mt-1">Lower first.</p>
              </div>
            </div>

            {/* Cover photo — the hero shot displayed at the top of the cabin card. */}
            <div>
              <Label className="overline text-primary/60">Cover photo</Label>
              <div className="mt-2 flex items-center gap-4">
                {form.cover_image_path ? (
                  <div className="relative">
                    <img src={fileUrl(form.cover_image_path)} alt="Cover" className="w-32 h-20 object-cover border border-border" data-testid="cf-cover-preview" />
                    <button
                      onClick={() => setForm({ ...form, cover_image_path: null })}
                      className="absolute -top-2 -right-2 bg-destructive text-destructive-foreground rounded-full w-5 h-5 flex items-center justify-center"
                      data-testid="cf-cover-remove"
                    ><X className="h-3 w-3" /></button>
                  </div>
                ) : (
                  <div className="w-32 h-20 border border-dashed border-border flex items-center justify-center text-[10px] text-muted-foreground">No cover</div>
                )}
                <label className="inline-flex items-center gap-2 px-4 h-10 border border-border cursor-pointer hover:border-primary text-sm">
                  <Upload className="h-4 w-4" /> {uploadingCover ? "Uploading…" : "Upload cover"}
                  <input type="file" accept="image/*" className="hidden" onChange={uploadCover} data-testid="cf-cover-upload" />
                </label>
              </div>
              {/* Live upload progress — a thin bar with % text so operators
                  know the upload is really moving (large iPad photos over
                  cellular used to feel hung otherwise). */}
              {uploadingCover && (
                <div className="mt-3" data-testid="cf-cover-progress">
                  <Progress value={coverProgress ?? 0} className="h-1.5" />
                  <div className="mt-1 flex items-center justify-between text-[10px] font-mono text-muted-foreground">
                    <span>Uploading cover photo…</span>
                    <span data-testid="cf-cover-progress-pct">{coverProgress ?? 0}%</span>
                  </div>
                </div>
              )}
            </div>

            {/* Additional photo gallery — displayed below the cover on future
                cabin-detail pages; multiple selection supported. */}
            <div>
              <Label className="overline text-primary/60">Photo gallery</Label>
              <div className="mt-2 flex flex-wrap gap-3">
                {(form.image_paths || []).map((p, idx) => (
                  <div key={`${p}-${idx}`} className="relative">
                    <img src={fileUrl(p)} alt={`Gallery ${idx + 1}`} className="w-20 h-20 object-cover border border-border" data-testid={`cf-gallery-${idx}`} />
                    <button
                      onClick={() => removeGalleryAt(idx)}
                      className="absolute -top-2 -right-2 bg-destructive text-destructive-foreground rounded-full w-5 h-5 flex items-center justify-center"
                      data-testid={`cf-gallery-remove-${idx}`}
                    ><X className="h-3 w-3" /></button>
                  </div>
                ))}
                <label className="w-20 h-20 border border-dashed border-border flex flex-col items-center justify-center gap-1 cursor-pointer hover:border-primary text-[10px] text-muted-foreground">
                  <Upload className="h-4 w-4" />
                  {uploadingGallery ? "Uploading…" : "Add"}
                  <input type="file" accept="image/*" multiple className="hidden" onChange={uploadGallery} data-testid="cf-gallery-upload" />
                </label>
              </div>
              <p className="text-[11px] text-muted-foreground mt-2">Select multiple — cover appears in the grid, gallery photos on the cabin's future detail view.</p>
              {uploadingGallery && galleryProgress && (
                <div className="mt-3" data-testid="cf-gallery-progress">
                  <Progress value={galleryProgress.percent ?? 0} className="h-1.5" />
                  <div className="mt-1 flex items-center justify-between text-[10px] font-mono text-muted-foreground">
                    <span>Photo {galleryProgress.index} of {galleryProgress.total}…</span>
                    <span data-testid="cf-gallery-progress-pct">{galleryProgress.percent ?? 0}%</span>
                  </div>
                </div>
              )}
            </div>

            <div>
              <Label className="overline text-primary/60">Sleeping berths</Label>
              <Input
                type="number"
                min={1}
                max={8}
                value={form.beds ?? 2}
                onChange={(e) => setForm({ ...form, beds: Math.max(1, Math.min(8, parseInt(e.target.value || "2", 10))) })}
                data-testid="cf-beds"
                className="mt-2"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Caps how many guests can share this cabin on the same charter. Between 1 and 8.
              </p>
            </div>
            <div>
              <Label className="overline text-primary/60">iPad PIN (4 digits) — for shared cabin iPad</Label>
              <Input value={form.ipad_pin || ""} onChange={(e) => setForm({ ...form, ipad_pin: e.target.value.replace(/\D/g, "").slice(0, 4) })} inputMode="numeric" maxLength={4} placeholder="Leave empty if no iPad" data-testid="cf-ipad-pin" className="mt-2 font-mono tracking-widest text-lg" />
              <p className="text-xs text-muted-foreground mt-1">Used on the cabin's shared iPad for one-time public login.</p>
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="cf-active" />
              <Label>Visible on guest login dropdown</Label>
            </div>
            <div>
              <Label className="overline text-primary/60">Occupancy status</Label>
              <p className="text-xs text-muted-foreground mt-1 mb-2">
                Guest / Available = spa is bookable · Crew use / Not available = spa iPad shows a polite locked screen.
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="cf-occupancy-grid">
                {OCCUPANCY_OPTIONS.map((opt) => {
                  const active = (form.occupancy_status || "guest") === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setForm({ ...form, occupancy_status: opt.value })}
                      className={`border p-3 text-left transition-colors ${active ? opt.bg + " ring-2 ring-offset-1 ring-current" : "bg-background border-border hover:border-primary/40"}`}
                      data-testid={`cf-occupancy-${opt.value}`}
                      aria-pressed={active}
                    >
                      <div className="font-medium text-sm">{opt.label}</div>
                      <div className="text-[10px] uppercase tracking-widest opacity-70 mt-0.5">{opt.desc}</div>
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={save} className="bg-primary hover:bg-primary/90" data-testid="cf-save">Save</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
